/* eslint-disable no-param-reassign */
import _ from 'underscore';
import deepEqual from 'fast-deep-equal';
import { Logger } from 'common/logger';
import { getSelectedParkData } from 'common/state.helper';
import { WS_STATUSES } from 'routes/OperationsCommercial/components/Commercial.constants';

export const FILTER_ISSUED = 'has-planned-work';

const makeLocationFilters = (parkItem) => {
    if (parkItem.locations?.length === 0) {
        return [];
    }

    const locationsFilters = [
        { name: '', displayName: 'All', isActive: true },
        ...(parkItem.locations?.map((location) => ({ name: location, displayName: location })) || []),
    ];

    return locationsFilters;
};

export const locationFilterAction = (name) => ({
    type: 'filter/locationFilter',
    payload: name,
});

export const secondaryStatusFilterAction = (name) => ({
    type: 'filter/secondaryStatusFilter',
    payload: name,
});

export const statusFilterAction = (name) => ({
    type: 'filter/statusFilter',
    payload: name,
});

export const sortAction = (name) => ({
    type: 'filter/sort',
    payload: name,
});

const setIsActive = (list, name) => list.map((item) => ({ ...item, isActive: item.name === name }));

export const toggleByName = (filter, name) =>
    filter.map((item) => {
        if (item.name === name) {
            item.isActive = !item.isActive;
            return item;
        }
        return item;
    });

export const setOnlyOne = (filter, name) =>
    filter.map((item) => {
        if (item.name === name) {
            item.isActive = true;
            return item;
        }
        item.isActive = false;
        return item;
    });

export const countAndUpdateStatusFilter = (turbines, statusFilters) => {
    const counters = _.countBy(_.where(turbines, { hidden: false }), 'status');

    const updatedFilters = statusFilters.map((filter) => {
        const f = { ...filter };
        const counter = counters[f.name];
        f.value = counter || 0;
        return f;
    });

    return updatedFilters;
};

export const countAndUpdateSecondaryStatusFilter = (turbines, statusFilters) => {
    const counters = _.where(turbines, { hidden: false }).reduce((count, turbine) => {
        turbine.secondaryStatus.forEach((filter) => {
            !count[filter] ? (count[filter] = 1) : (count[filter] += 1);
        });
        return count;
    }, {});

    const updatedFilters = statusFilters.map((filter) => {
        const f = { ...filter };
        const counter = counters[f.name];
        f.value = counter || 0;
        return f;
    });

    return updatedFilters;
};

export const initialState = {
    activeSortName: 'numericName',
    locationFilters: [],
    statusFilters: [
        { name: WS_STATUSES.AVAILABLE, displayName: 'Available', value: 'N/A' },
        { name: WS_STATUSES.IN_SERVICE, displayName: 'In Service', value: 'N/A' },
        { name: WS_STATUSES.ALARM_RUNNING, displayName: 'Alarm: Running', value: 'N/A' },
        { name: WS_STATUSES.CURTAILED, displayName: 'Curtailed', value: 'N/A' },
        { name: WS_STATUSES.NO_COMM, displayName: 'No Comms', value: 'N/A' },
        { name: WS_STATUSES.STOPPED, displayName: 'Stopped', value: 'N/A' },
        { name: WS_STATUSES.ALARM_STOPPED, displayName: 'Alarm: Stopped', value: 'N/A' },
        { name: WS_STATUSES.STOPPED_MANUAL, displayName: 'Stopped: Manual', value: 'N/A' },
    ],
    secondaryStatusFilters: [
        { name: 'is-unavailable', displayName: 'Unavailable', value: 'N/A' },
        { name: 'is-inconsistent', displayName: 'Inconsistent', value: 'N/A' },
        { name: 'has-exception', displayName: 'Exception', value: 'N/A' },
        { name: FILTER_ISSUED, displayName: 'Issued', value: 'N/A' },
        { name: 'is-occupied', displayName: 'Occupied', value: 'N/A' },
        { name: 'has-restriction', displayName: 'Restricted Zone', value: 'N/A' },
        { name: 'has-note', displayName: 'Note', value: 'N/A' },
    ],
    sortOptions: [
        { name: 'numericName', displayName: 'Name', isActive: true },
        { name: 'string', displayName: 'String' },
        { name: 'active_power.value', displayName: 'Active Power' },
        { name: 'is_released.value', displayName: 'Released' },
        { name: 'person_count.value', displayName: 'People' },
    ],
};

const filter = {
    name: 'filter',
    state: { ...initialState },
    reducers: {
        updateAll(state, { activeSortName, locationFilters, statusFilters, secondaryStatusFilters, sortOptions }) {
            return {
                activeSortName,
                locationFilters,
                secondaryStatusFilters,
                statusFilters,
                sortOptions,
            };
        },
        updateStatusFilter(state, { updatedFilters }) {
            state.statusFilters = [...updatedFilters];
        },
        updateSecondaryStatusFilter(state, { updatedFilters }) {
            state.secondaryStatusFilters = [...updatedFilters];
        },
        setLocationFilter(state, { name }) {
            state.locationFilters = [...setOnlyOne(state.locationFilters, name)];
        },
        setToggleStatusFilter(state, { name }) {
            state.statusFilters = toggleByName(state.statusFilters, name);
        },
        setToggleSecondaryStatusFilter(state, { name }) {
            state.secondaryStatusFilters = toggleByName(state.secondaryStatusFilters, name);
        },
        setActiveSort(state, name) {
            Logger.log('Updated active sort', name);
            return {
                ...state,
                activeSortName: name,
                sortOptions: [...setIsActive(state.sortOptions, name)],
            };
        },
    },

    effects: (dispatch) => ({
        calculateStatus: async (__, rootState) => {
            Logger.log('Calculating statuses counts');
            const turbinesStore = rootState.turbines;

            const turbines = turbinesStore.list.map((id) => turbinesStore.byId[id]).filter(Boolean); // Ensure we only get valid turbine objects

            const filters = rootState.filter;

            const updatedFilters = countAndUpdateStatusFilter(turbines, filters.statusFilters);
            const updatedSecondaryFilters = countAndUpdateSecondaryStatusFilter(
                turbines,
                filters.secondaryStatusFilters
            );

            if (!deepEqual(updatedFilters, filters.statusFilters)) {
                dispatch.filter.updateStatusFilter({ updatedFilters });
            } else {
                Logger.debug('statusFilters are equal.', updatedFilters, filters.statusFilters);
            }

            if (!deepEqual(updatedSecondaryFilters, filters.secondaryStatusFilters)) {
                dispatch.filter.updateSecondaryStatusFilter({ updatedFilters: updatedSecondaryFilters });
            } else {
                Logger.debug(
                    'secondaryStatusFilters are equal.',
                    updatedSecondaryFilters,
                    filters.secondaryStatusFilters
                );
            }
        },

        locationFilter: (name) => {
            dispatch.filter.setLocationFilter({ name });
            dispatch.turbines.showOnLocation({ name });

            window.analytics.track('location filter', { name });
        },

        statusFilter: (name, rootState) => {
            dispatch.filter.setToggleStatusFilter({ name });
            dispatch.turbines.checkFilters();

            const filters = rootState.filter.statusFilters;
            const filter = filters.find((item) => item.name === name);
            const state = filter?.isActive ? 'on' : 'off';

            Logger.log(`Filter ${name} is ${state}`);
            window.analytics.track(`state filter ${state}`, {
                name,
            });
        },

        secondaryStatusFilter: async (name, rootState) => {
            dispatch.filter.setToggleSecondaryStatusFilter({ name });
            dispatch.turbines.checkFilters();

            const filters = rootState.filter.secondaryStatusFilters;
            const filter = filters.find((item) => item.name === name);
            const state = filter?.isActive ? 'on' : 'off';

            Logger.log(`Secondary Filter ${name} is ${state}`);
            window.analytics.track(`secondary filter ${state}`, {
                name,
            });
        },

        reset: (__, rootState) => {
            Logger.info('Reset filters');

            const parkData = getSelectedParkData(rootState);

            dispatch.filter.updateAll({
                ...initialState,
                locationFilters: makeLocationFilters(parkData),
            });
        },
        sort(name) {
            dispatch.filter.setActiveSort(name);
            dispatch.turbines.sortTurbines(name);
            window.analytics.track('sort', { name });
        },
    }),
};

export default filter;
